/**
 * The manual rail — recording money that already moved off platform.
 *
 * This ships FIRST in the console's sequence, on purpose. It is the epic's
 * stated first business benefit and needs no new product code: Finance logs the
 * consultation payouts it already makes by hand, and the AP ledger stops being
 * empty. It also sidesteps Connect readiness entirely — nothing here calls
 * Stripe.
 *
 * Two rules shape the whole form.
 *
 * **`paidAt` is the sharpest edge on this screen.** It defaults to now, is
 * deliberately backdatable (the 1099 ledger buckets on `YEAR(paid_at)`), and the
 * api's own comment says there is no correction path once the row is paid — the
 * audit trail that would have surfaced a mistyped year is the thing the typo
 * hides. So the confirm step names the tax year IN WORDS, not just a formatted
 * date: "2025" is much harder to skim past than "06/15/2025".
 *
 * **A duplicate returns the row that already exists.** Recording against a live
 * `(sourceType, sourceId, kind)` triple silently returns the PRE-EXISTING row,
 * dropping the description just typed. So the result step renders what came
 * back rather than echoing the inputs, and says plainly when the two differ.
 * Echoing the submission would tell an operator they had recorded a payment
 * they had not.
 *
 * Primitive gap (already flagged in `ResolveDisputeExpander`): componentsv2 has
 * no single-select form field, so the two enum pickers are styled native
 * `<select>`s rather than a silently-extended primitive set.
 */
import { useMutation } from '@apollo/client';
import { useId, useState } from 'react';

import {
  ExpertPaymentKind,
  ExpertPaymentSourceType,
  type RecordManualExpertPaymentMutation,
} from '@/__generated__/graphql';
import Button from '@/componentsv2/Button';
import Input from '@/componentsv2/Input';

import EnumField from '../EnumField';
import { type SettlementError, describeSettlementError } from '../errors';
import { formatDate, formatMoney, taxYearOf } from '../format';
import { RECORD_MANUAL_EXPERT_PAYMENT } from '../operations';
import { KIND_OPTIONS, SOURCE_TYPE_OPTIONS } from '../vocabulary';
import {
  type ManualPaymentDraft,
  type ManualPaymentErrors,
  hasErrors,
  parseAmountToCents,
  parsePaidAt,
  validateManualPayment,
} from './validation';

type RecordedPayment = RecordManualExpertPaymentMutation['recordManualExpertPayment'];

const EMPTY: ManualPaymentDraft = {
  payeeUserId: '',
  sourceId: '',
  description: '',
  externalRef: '',
  amount: '',
  paidAt: '',
  reasoning: '',
};

interface RecordManualPaymentFormProps {
  /** Injected by `Dialog`, which clones its child to add it. */
  onClose?: () => void;
  /** Called after a successful record, so the queue can re-read itself. */
  onRecorded?: () => void;
}

export default function RecordManualPaymentForm({
  onClose,
  onRecorded,
}: RecordManualPaymentFormProps): JSX.Element {
  const [draft, setDraft] = useState<ManualPaymentDraft>(EMPTY);
  const [kind, setKind] = useState<ExpertPaymentKind>(ExpertPaymentKind.WorkFee);
  const [sourceType, setSourceType] = useState<ExpertPaymentSourceType>(
    ExpertPaymentSourceType.Consultation
  );
  const [errors, setErrors] = useState<ManualPaymentErrors>({});
  const [confirming, setConfirming] = useState(false);
  const [recorded, setRecorded] = useState<RecordedPayment | null>(null);
  const [failure, setFailure] = useState<SettlementError | null>(null);

  const [record, { loading }] = useMutation(RECORD_MANUAL_EXPERT_PAYMENT);
  const ids = useId();

  const set = (field: keyof ManualPaymentDraft) => (value: string) => {
    setDraft((prev) => ({ ...prev, [field]: value }));
    // Clear this field's error as it is edited; keep the others, so the summary
    // does not flicker away the moment any key is pressed.
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  function review() {
    const found = validateManualPayment(draft);
    setErrors(found);
    if (!hasErrors(found)) setConfirming(true);
  }

  // Validated in `review` before this screen can be reached.
  const effectivePaidAt = draft.paidAt ? parsePaidAt(draft.paidAt) : null;
  const taxYear = taxYearOf(effectivePaidAt ?? new Date());
  const cents = parseAmountToCents(draft.amount);

  async function submit() {
    setFailure(null);
    try {
      const res = await record({
        variables: {
          payeeUserId: draft.payeeUserId.trim(),
          kind,
          sourceType,
          sourceId: draft.sourceId.trim(),
          amount: { cents: String(cents), currency: 'USD' },
          externalRef: draft.externalRef.trim(),
          description: draft.description.trim(),
          reasoning: draft.reasoning.trim() || null,
          // Omitted entirely rather than sent as now: the api's default IS now,
          // and sending a client clock would make the tax year depend on the
          // operator's machine.
          paidAt: effectivePaidAt ? effectivePaidAt.toISOString() : null,
        },
      });
      const row = res.data?.recordManualExpertPayment ?? null;
      setRecorded(row);
      onRecorded?.();
    } catch (err) {
      setFailure(describeSettlementError(err, 'record_manual'));
      setConfirming(false);
    }
  }

  if (recorded) {
    return <RecordedResult recorded={recorded} draft={draft} onClose={onClose} />;
  }

  if (confirming) {
    return (
      <div className="flex flex-col gap-16">
        <p className="text-on-surface body-1">
          Recording a payment of{' '}
          <strong>{formatMoney({ cents: String(cents), currency: 'USD' })}</strong> to expert{' '}
          <strong>{draft.payeeUserId.trim()}</strong>, referenced{' '}
          <strong>{draft.externalRef.trim()}</strong>.
        </p>

        <div className="rounded-md border border-yellow-700/40 bg-yellow-50 p-16 dark:bg-yellow-900/20">
          <p className="text-yellow-900 subtitle-1 dark:text-yellow-200">
            This will be recorded in tax year {taxYear}.
          </p>
          <p className="mt-4 text-yellow-900 body-2 dark:text-yellow-200">
            <span>
              {effectivePaidAt
                ? `Dated ${formatDate(effectivePaidAt.toISOString())}.`
                : 'Dated today.'}
            </span>{' '}
            <span>
              The 1099 ledger buckets on this date, and there is no correction path once the payment
              is recorded — it cannot be edited, voided or reversed on the platform.
            </span>
          </p>
        </div>

        <div className="flex flex-wrap gap-8">
          <Button onClick={() => void submit()} loading={loading} disabled={loading}>
            {`Record payment in ${taxYear}`}
          </Button>
          <Button variant="secondary" onClick={() => setConfirming(false)} disabled={loading}>
            Back
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-16">
      <p className="text-on-surface-muted body-2">
        For money that has already moved off platform — a wire or a check. Nothing here calls
        Stripe. Amount and payee are frozen the moment this is recorded.
      </p>

      {failure ? <FailureNotice failure={failure} /> : null}

      <Input
        label="Expert user id"
        required
        value={draft.payeeUserId}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => set('payeeUserId')(e.target.value)}
        error={!!errors.payeeUserId}
        helperText={errors.payeeUserId}
      />

      <EnumField
        id={`${ids}-source-type`}
        label="What is being paid for"
        value={sourceType}
        options={SOURCE_TYPE_OPTIONS}
        onChange={(v) => setSourceType(v as ExpertPaymentSourceType)}
      />

      <Input
        label="Source id"
        required
        value={draft.sourceId}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => set('sourceId')(e.target.value)}
        error={!!errors.sourceId}
        helperText={errors.sourceId ?? 'The id of the consultation, response or submission.'}
      />

      <EnumField
        id={`${ids}-kind`}
        label="Kind"
        value={kind}
        options={KIND_OPTIONS}
        onChange={(v) => setKind(v as ExpertPaymentKind)}
      />

      <Input
        label="Amount (USD)"
        required
        value={draft.amount}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => set('amount')(e.target.value)}
        error={!!errors.amount}
        helperText={errors.amount ?? 'USD only in v1. This cannot be changed afterwards.'}
      />

      <Input
        label="External reference"
        required
        value={draft.externalRef}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => set('externalRef')(e.target.value)}
        error={!!errors.externalRef}
        helperText={errors.externalRef ?? 'The wire or check reference this payment traces to.'}
      />

      <Input
        label="Description"
        required
        value={draft.description}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => set('description')(e.target.value)}
        error={!!errors.description}
        helperText={errors.description ?? 'The ledger line item Finance reads and exports to QBO.'}
      />

      <Input
        label="Date the money moved"
        value={draft.paidAt}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => set('paidAt')(e.target.value)}
        error={!!errors.paidAt}
        helperText={
          errors.paidAt ??
          'YYYY-MM-DD. Leave blank for today. Backdate a prior-year wire — this picks its tax year.'
        }
      />

      <Input
        label="Note (optional)"
        value={draft.reasoning}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => set('reasoning')(e.target.value)}
        helperText="Evidence for whoever reads this row later. Appended to the row's trail."
      />

      <div className="flex flex-wrap gap-8">
        <Button onClick={review}>Review</Button>
        {onClose ? (
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
        ) : null}
      </div>
    </div>
  );
}

function FailureNotice({ failure }: { failure: SettlementError }): JSX.Element {
  return (
    <div className="rounded-md border border-red-700/40 bg-red-50 p-16 dark:bg-red-900/15">
      <p className="text-red-900 body-2 dark:text-red-200">{failure.message}</p>
    </div>
  );
}

/**
 * What actually landed.
 *
 * Renders the returned row, not the submission — and when the api returned a
 * pre-existing row instead of creating one, says so. The tell is a description
 * that differs from what was typed: `RecordManualPayment` is idempotent on the
 * live `(sourceType, sourceId, kind)` triple and returns the existing row
 * unchanged, silently dropping the submitted description.
 */
function RecordedResult({
  recorded,
  draft,
  onClose,
}: {
  recorded: RecordedPayment;
  draft: ManualPaymentDraft;
  onClose?: () => void;
}): JSX.Element {
  const preExisting = recorded.description !== draft.description.trim();

  return (
    <div className="flex flex-col gap-16">
      {preExisting ? (
        <div className="rounded-md border border-yellow-700/40 bg-yellow-50 p-16 dark:bg-yellow-900/20">
          <p className="text-yellow-900 subtitle-1 dark:text-yellow-200">
            A payment for this source already existed.
          </p>
          <p className="mt-4 text-yellow-900 body-2 dark:text-yellow-200">
            Nothing new was created and no money moved twice. What you entered was not saved — the
            existing record is shown below, exactly as it stands.
          </p>
        </div>
      ) : (
        <p className="text-on-surface subtitle-1">Payment recorded.</p>
      )}

      <dl className="flex flex-col gap-12">
        <Field label="Payee">
          {recorded.payeeIdentity?.name
            ? `${recorded.payeeIdentity.name} (${recorded.payee})`
            : recorded.payee}
        </Field>
        <Field label="Amount">{formatMoney(recorded.amount)}</Field>
        <Field label="Reference">{recorded.providerRef ?? '—'}</Field>
        <Field label="Description">{recorded.description}</Field>
        <Field label="Tax year">
          {recorded.paidAt ? String(taxYearOf(new Date(recorded.paidAt))) : '—'}
        </Field>
        <Field label="Dated">{recorded.paidAt ? formatDate(recorded.paidAt) : '—'}</Field>
      </dl>

      {onClose ? <Button onClick={onClose}>Done</Button> : null}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <div className="flex flex-col gap-2">
      <dt className="text-on-surface-muted util-caption">{label}</dt>
      <dd className="text-on-surface body-2">{children}</dd>
    </div>
  );
}
